import { useEffect, useMemo, useRef, useState } from "react";
import { SelectInput, useGetList, useRecordContext, useTranslate } from "react-admin";
import { useFormContext } from "react-hook-form";

import type { Location } from "../generated/graphql";

const MAX_LOCATIONS_TO_FETCH = 1000;
const ORGANIZATION_LOCATION_TYPE = "ORGANIZATION";

export type LocationRecord = Pick<Location, "id" | "name" | "type" | "partOfId"> & {
  externalId?: string;
  organizationId?: string;
};

type LocationTree = {
  roots: LocationRecord[];
  childrenMap: Map<string, LocationRecord[]>;
};

type LocationHierarchyEntry = {
  level: number;
  locationId: string;
  locationType?: string | null;
};

const isOrganizationLocation = (location: Pick<LocationRecord, "type">): boolean =>
  location.type === ORGANIZATION_LOCATION_TYPE;

export const buildPhysicalLocationTree = (locations: LocationRecord[]): LocationTree => {
  const physicalLocations = locations.filter(location => !isOrganizationLocation(location));
  const physicalLocationIds = new Set(physicalLocations.map(location => location.id));
  const childrenMap = new Map<string, LocationRecord[]>();
  const roots: LocationRecord[] = [];

  physicalLocations.forEach(location => {
    if (!location.partOfId || !physicalLocationIds.has(location.partOfId)) {
      roots.push(location);
      return;
    }

    const siblings = childrenMap.get(location.partOfId) || [];
    siblings.push(location);
    childrenMap.set(location.partOfId, siblings);
  });

  return { roots, childrenMap };
};

export const getPhysicalLocationPath = (hierarchy: LocationHierarchyEntry[]): string[] =>
  hierarchy
    .filter(location => location.locationType !== ORGANIZATION_LOCATION_TYPE)
    .sort((a, b) => b.level - a.level)
    .map(location => location.locationId);

export const LocationHierarchyFields = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const { register, setValue } = useFormContext();

  // Track the selected path through the hierarchy as an array
  // Each index represents a level in the hierarchy
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const hasInitializedPath = useRef(false);

  const { data: allLocations } = useGetList<LocationRecord>("Location", {
    filter: {
      organizationId: record?.organizationId,
    },
    pagination: { page: 1, perPage: MAX_LOCATIONS_TO_FETCH },
  });

  // Build location hierarchy tree
  const locationTree = useMemo(() => buildPhysicalLocationTree(allLocations || []), [allLocations]);

  // Initialize from existing locationHierarchy when editing
  useEffect(() => {
    if (hasInitializedPath.current || !record || !allLocations) return;

    hasInitializedPath.current = true;
    const path = getPhysicalLocationPath(record.locationHierarchy || []);
    setSelectedPath(path);

    // Set the most specific location as locationId
    if (path.length > 0) {
      setValue("locationId", path[path.length - 1]);
    }
  }, [allLocations, record, setValue]);

  // The form resets after its record loads. Restore the visible hierarchy
  // fields on the following render so that clearing an existing assignment
  // produces a real change event.
  useEffect(() => {
    selectedPath.forEach((locationId, level) => setValue(`location_level_${level}`, locationId));
  }, [selectedPath, setValue]);

  // Get available choices for a given level
  const getChoicesForLevel = (level: number): LocationRecord[] => {
    if (level === 0) {
      return locationTree.roots;
    }

    const parentId = selectedPath[level - 1];
    if (!parentId) return [];

    return locationTree.childrenMap.get(parentId) || [];
  };

  // Calculate how many levels to display
  // Show current path + 1 more level if children exist
  const maxLevel = useMemo(() => {
    let level = selectedPath.length;
    // Check if current selection has children
    if (selectedPath.length > 0) {
      const lastSelected = selectedPath[selectedPath.length - 1];
      const hasChildren = locationTree.childrenMap.has(lastSelected);
      if (hasChildren) {
        level = selectedPath.length + 1;
      }
    } else {
      // No selection yet, show first level
      level = 1;
    }
    return level;
  }, [selectedPath, locationTree.childrenMap]);

  const handleLevelChange = (level: number, locationId: string) => {
    // Emptying the first level means no physical assignment. Emptying a child
    // level moves the assignment back to its selected physical parent.
    const newPath = locationId ? [...selectedPath.slice(0, level), locationId] : selectedPath.slice(0, level);
    setSelectedPath(newPath);

    // Set locationId to the most specific selected location
    setValue("locationId", newPath.length > 0 ? newPath[newPath.length - 1] : null, { shouldDirty: true });
  };

  // Generate label for each level based on location type
  const getLabelForLevel = (level: number): string => {
    const choices = getChoicesForLevel(level);
    if (choices.length === 0) return translate("location");

    // Use the type of the first location as the label
    const locationType = choices[0].type;
    if (locationType) {
      return translate(`locationType.${locationType}`);
    }

    return translate("location");
  };

  return (
    <>
      {Array.from({ length: maxLevel }, (_, level) => {
        const choices = getChoicesForLevel(level);
        const isDisabled = level > 0 && !selectedPath[level - 1];
        return (
          <SelectInput
            key={level}
            source={`location_level_${level}`}
            label={getLabelForLevel(level)}
            choices={choices}
            optionText="name"
            optionValue="id"
            fullWidth
            emptyText={level === 0 ? translate("patient.locationNotAssigned") : undefined}
            disabled={isDisabled || choices.length === 0}
            onChange={eventOrValue =>
              handleLevelChange(level, typeof eventOrValue === "string" ? eventOrValue : eventOrValue.target.value)
            }
          />
        );
      })}

      {/* Hidden field to store the final locationId */}
      <input type="hidden" {...register("locationId")} />
    </>
  );
};
